import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft } from 'lucide-react';
import { Breadcrumbs, type BreadcrumbItem } from './Breadcrumbs';

export interface PageHeaderProps {
  variant?: 'scroll' | 'fullscreen';
  backTo?: string;
  onBack?: () => void;
  breadcrumbs?: BreadcrumbItem[];
  title: ReactNode;
  subtitle?: ReactNode;
  actions?: ReactNode;
  tabs?: ReactNode;
  children?: ReactNode;
}

export function PageHeader({
  variant = 'scroll',
  backTo,
  onBack,
  breadcrumbs,
  title,
  subtitle,
  actions,
  tabs,
  children,
}: PageHeaderProps) {
  const header = (
    <header
      className={
        variant === 'fullscreen'
          ? 'shrink-0 border-b border-surface-border bg-white shadow-sm'
          : 'sticky top-0 z-20 -mx-6 -mt-6 md:-mx-8 md:-mt-8 mb-6 border-b border-surface-border bg-white/95 backdrop-blur shadow-sm'
      }
    >
      <div className="flex w-full flex-wrap items-center gap-3 px-6 py-3">
        {backTo ? (
          <Link
            to={backTo}
            className="inline-flex shrink-0 items-center gap-1 text-sm text-ink-secondary hover:text-ink-primary"
          >
            <ChevronLeft className="h-4 w-4" />
            Back
          </Link>
        ) : onBack ? (
          <button
            type="button"
            onClick={onBack}
            className="inline-flex shrink-0 items-center gap-1 text-sm text-ink-secondary hover:text-ink-primary"
          >
            <ChevronLeft className="h-4 w-4" />
            Back
          </button>
        ) : null}
        <div className="min-w-0 flex-1">
          {breadcrumbs && breadcrumbs.length > 0 && (
            <div className="mb-0.5">
              <Breadcrumbs items={breadcrumbs} />
            </div>
          )}
          {typeof title === 'string' ? (
            <h1 className="truncate text-base font-semibold text-ink-primary">{title}</h1>
          ) : (
            title
          )}
          {subtitle && (
            typeof subtitle === 'string' ? (
              <p className="truncate text-[11px] text-ink-muted">{subtitle}</p>
            ) : (
              subtitle
            )
          )}
        </div>
        {actions && <div className="flex shrink-0 items-center gap-2">{actions}</div>}
      </div>
      {tabs && (
        <div className="border-t border-surface-border/60 bg-surface-canvas/40 px-6">
          {tabs}
        </div>
      )}
    </header>
  );

  if (variant === 'fullscreen') {
    return (
      <div className="-m-6 md:-m-8 flex h-[calc(100vh-60px)] flex-col bg-surface-canvas">
        {header}
        <div className="flex-1 overflow-y-auto">{children}</div>
      </div>
    );
  }

  return header;
}
